import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cases } from '../data/cases';
import { imageUrls } from '../utils/imageOptimizer';
import SEO from '../components/SEO';

export default function Cases() {
  return (
    <main data-testid="cases-page" className="pt-24">
      <SEO 
        title="Cases & Referencer"
        pathname="/cases"
        description="Udforsk vores udvalgte projekter og referencer. Se hvordan vi har hjulpet brands som Boghallen, Cabinn, Politiken og Matas med professionel skiltning og wayfinding."
      />
      {/* Hero */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6 animate-fade-in">
            Referencer
          </p>
          <h1 
            data-testid="cases-title"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-[#1A1A1A] mb-6 animate-fade-in-up max-w-3xl"
          >
            Udvalgte projekter
          </h1>
          <p className="text-[#666666] text-lg max-w-2xl animate-fade-in-up stagger-2">
            Vi er stolte af at samarbejde med nogle af Danmarks førende virksomheder og institutioner. Se et udvalg af vores projekter.
          </p>
        </div>
      </section>

      {/* Cases List - Scanglas inspired layout with space */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="divide-y divide-[#F0F0F0]">
              {cases.map((caseItem, index) => (
                <Link
                  key={caseItem.id}
                  to={`/cases/${caseItem.id}`}
                  data-testid={`case-item-${caseItem.id}`}
                  className="block group"
                >
                  <article className={`py-12 md:py-20 px-6 md:px-8 ${
                    index % 2 === 0 ? '' : 'bg-[#FAFAFA]'
                  }`}>
                    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                      index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                    }`}>
                      {/* Image */}
                      <div className={`lg:col-span-7 ${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
                        <div className="overflow-hidden aspect-[16/10]">
                          <img
                            src={imageUrls.large(caseItem.image)}
                            alt={caseItem.title}
                            className="w-full h-full object-cover object-center transform group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                            loading="lazy"
                          />
                        </div>
                      </div>

                      {/* Content */}
                      <div className={`lg:col-span-5 ${index % 2 !== 0 ? 'lg:order-1 lg:pr-8' : 'lg:pl-8'}`}>
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-xs text-[#999999] tracking-[0.15em] uppercase">
                            {caseItem.category}
                          </span>
                          <span className="text-[#E5E5E5]">—</span>
                          <span className="text-xs text-[#999999]">
                            {caseItem.year}
                          </span>
                        </div>
                        
                        <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-4 group-hover:text-[#666666] transition-colors duration-300">
                          {caseItem.title}
                        </h2>
                        
                        <p className="text-[#666666] leading-relaxed mb-6 line-clamp-3">
                          {caseItem.description}
                        </p>

                        <div className="flex items-center gap-2 text-[#1A1A1A] text-sm font-medium group-hover:gap-4 transition-all duration-300">
                          <span>Se projekt</span>
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6">
            Har du et projekt i tankerne?
          </h2>
          <p className="text-white/60 mb-10 max-w-md mx-auto">
            Lad os skabe noget fantastisk sammen. Vi rådgiver gerne – uanset projektets størrelse.
          </p>
          <Link
            to="/kontakt"
            data-testid="cases-cta"
            className="btn-light inline-flex items-center gap-2"
          >
            Start dit projekt
          </Link>
        </div>
      </section>
    </main>
  );
}
